import React, { useEffect, useRef, useState } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import ReactDOM from 'react-dom'
import styled from 'styled-components'

import { useDidUpdate } from 'utils/hooks'

import CenteredLoader from 'components/lib/CenteredLoader'
import ActionIcon from 'components/lib/DesignSystem/ActionIcon'
import Button from 'components/lib/DesignSystem/Button'

import { Close, FillCheck, FillError, FillInfo, FillWarning } from './Icons'

const ESCAPE = 27

const iconProps = {
  id: 'Modal_Type_Icon',
  width: '24px',
  height: '24px',
}

const mapNameByType = {
  warning: <FillWarning {...iconProps} fill="#FFA800" />,
  error: <FillError {...iconProps} fill="#E44258" />,
  info: <FillInfo {...iconProps} fill="#00AFFF" />,
  valid: <FillCheck {...iconProps} fill="#4D69FF" />,
  note: <FillError {...iconProps} fill="#565B6D" />,
}

const Modal = ({
  className,
  backgroundUrl,
  id,
  modalStyle,
  contentCustomStyle,
  loader = false,
  showCloseBtn = true,
  footerContainerStyles,

  trigger,
  open = false,
  onClose,
  onOpen,

  title,
  children,
  footer,
  modalType,

  confirm,
  customConfirmNo,
  customConfirmYes,
  customYesId,
  customNoId,
  confirmDisabled,
  closeOnConfirm,

  customMountNode,
  isScrollContent = true,
}) => {
  const [isOpen, setIsOpen] = useState(open || false)

  useEffect(() => {
    setIsOpen(open)
  }, [open])

  const mountNode = customMountNode || document.body
  const contentRef = useRef()

  const handleKeyDown = (event) => {
    if (event.keyCode === ESCAPE) {
      setIsOpen(false)
      if (typeof onClose === 'function') onClose()
    }
  }

  const handleOpen = () => {
    setIsOpen(true)
    document.querySelector('body').classList.add('oh')
    document.addEventListener('keydown', handleKeyDown)
    if (typeof onOpen === 'function') onOpen()
  }

  const handleClose = () => {
    setIsOpen(false)
    document.querySelector('body').classList.remove('oh')
    document.removeEventListener('keydown', handleKeyDown)
    if (typeof onClose === 'function') onClose()
  }

  const handleConfirm = () => {
    if (closeOnConfirm) handleClose()
    if (open) onClose()
    confirm()
  }

  const stopPropagation = (e) => e.stopPropagation()

  const ccn = classnames('is-modal', className, 'box-shadow-modal')

  const isItConfirmModal = typeof confirm === 'function'

  useEffect(() => {
    if (children && contentRef.current && isScrollContent) {
      contentRef.current.scrollTo(0, 0)
    }
  }, [children])

  useDidUpdate(() => {
    if (open) handleOpen()
    else handleClose()

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (open) {
        document.querySelector('body').classList.remove('oh')
      }
    }
  }, [open])

  return (
    <>
      {isOpen &&
        ReactDOM.createPortal(
          <div
            id={id}
            ref={() => {
              window.closeModal = () => handleClose()
            }}
            className={ccn}
            style={{ background: backgroundUrl }}
          >
            <div style={modalStyle} className="is-modal" onClick={stopPropagation}>
              {showCloseBtn && (
                <ActionIcon
                  id="x-modal_close"
                  className="close-btn"
                  Icon={Close}
                  onClick={handleClose}
                />
              )}

              <div className="modal-header-container">
                {modalType && mapNameByType[modalType || 'info']}
                <div
                  id="modal_header_title"
                  style={{ marginLeft: modalType ? '12px' : '0' }}
                  className="typography-title-1"
                >
                  {title}
                </div>
              </div>
              <div
                ref={contentRef}
                className={classnames(
                  'modal-content-container',
                  'styled-scrollbar',
                  isItConfirmModal || footer ? 'with-footer' : ''
                )}
                style={contentCustomStyle}
              >
                {loader ? (
                  <CenteredLoader style={{ height: '200px' }} />
                ) : (
                  <span className="typography-body-1">
                    {React.cloneElement(children, {
                      modalCloseSelf: handleClose,
                    })}
                  </span>
                )}
              </div>

              {(footer || isItConfirmModal) && (
                <div style={{ ...footerContainerStyles }} className="modal-footer-container">
                  {isItConfirmModal && (
                    <>
                      <Button
                        text={customConfirmNo || 'No'}
                        styleType="secondary"
                        onClick={handleClose}
                        style={{ marginRight: '15px' }}
                        id={customNoId || 'Modal_Confirm_No_Btn'}
                      />
                      <Button
                        disabled={confirmDisabled}
                        text={customConfirmYes || 'Yes'}
                        onClick={handleConfirm}
                        id={customYesId || 'Modal_Confirm_Yes_Btn'}
                      />
                    </>
                  )}
                  {footer}
                </div>
              )}
            </div>
          </div>,
          mountNode
        )}
      {trigger &&
        React.cloneElement(trigger, {
          onClick: handleOpen,
        })}
    </>
  )
}

Modal.propTypes = {
  className: PropTypes.string,
  backgroundUrl: PropTypes.string,
  id: PropTypes.string,
  modalStyle: PropTypes.object,
  loader: PropTypes.bool,
  showCloseBtn: PropTypes.bool,
  footerContainerStyles: PropTypes.object,
  trigger: PropTypes.element,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,

  title: PropTypes.string,
  children: PropTypes.node,
  footer: PropTypes.node,
  // Can be error, warning, info, valid or note
  modalType: PropTypes.string,
  withTabs: PropTypes.bool,

  confirm: PropTypes.func,
  customConfirmNo: PropTypes.string,
  customConfirmYes: PropTypes.string,
  customYesId: PropTypes.string,
  customNoId: PropTypes.string,
  confirmDisabled: PropTypes.bool,

  customMountNode: PropTypes.object,
  isScrollContent: PropTypes.bool,
}

export default styled(Modal)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${(props) => props.theme.zIndexModal};
  display: flex;
  background-color: ${(props) => props.theme.modalBackgroundColor};

  .close-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 1;
  }

  .is-modal {
    color: ${({ theme }) => theme.ds_gray_1};
    width: ${({ width }) => width || '500px'};
    min-width: ${({ minWidth }) => minWidth || '500px'};
    height: ${(props) => props.height || 'auto'};
    max-height: calc(100vh - 30px);
    margin: auto;
    border-radius: 6px;
    position: relative;
    display: grid;
    grid-template-rows: auto 1fr;
    background: white;

    @media only screen and (max-width: 750px) {
      width: 100%;
      min-width: auto;
    }

    .modal-header-container {
      display: flex;
      font-weight: ${(props) => props.theme.semibold};
      font-size: 20px;
      height: fit-content;
      padding: 20px 30px;
      border-bottom: 1px solid #e6e9ee;
      display: flex;
    }

    .modal-content-container {
      //handling scroll
      margin-right: 4px;
      padding: 0 26px 0 30px;
      padding-top: 30px;
      max-height: 750px;
      overflow-x: hidden;
      overflow-y: auto;
      line-height: 22px;
      text-align: left;
    }

    .modal-content-container.with-footer {
      padding-bottom: 30px;
    }

    .modal-footer-container {
      padding: 14px 30px;
      border-top: 1px solid #e6e9ee;
      text-align: right;
    }

    // When footer cannot be a props of modal
    .modal-footer-external {
      width: calc(100% + 60px);
      margin-top: 32px;
      padding: 14px 30px;
      border-top: 1px solid #e6e9ee;
      position: relative;
      right: 30px;
      text-align: right;
    }
  }
`
